// ─── AI Coach Service ─────────────────────────────────────
// Routes through backend to keep API key secure

const API_BASE = import.meta.env.VITE_APP_URL || ''

export const aiService = {
  chat: async ({ message, userContext, conversationHistory = [] }) => {
    const res = await fetch(`${API_BASE}/api/ai/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message, userContext, conversationHistory }),
    })
    return res.json()
  },

  getInsights: async (userContext) => {
    const res = await fetch(`${API_BASE}/api/ai/insights`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userContext }),
    })
    return res.json()
  },
}

// ─── System prompt builder ─────────────────────────────────
export const buildSystemPrompt = (userContext) => `
You are the PeppermintFi AI Coach — a warm, knowledgeable personal finance advisor.
You have access to the user's real financial data and provide personalized, actionable advice.

USER FINANCIAL SNAPSHOT:
- Net Worth: $${userContext.netWorth?.toLocaleString() || 'Unknown'}
- Monthly Income: $${userContext.monthlyIncome?.toLocaleString() || 'Unknown'}
- Monthly Spending: $${userContext.monthlySpend?.toLocaleString() || 'Unknown'}
- Savings Rate: ${userContext.savingsRate || 'Unknown'}%
- Credit Score: ${userContext.creditScore || 'Unknown'}
- Plan: ${userContext.plan || 'free'}

TOP SPENDING CATEGORIES THIS MONTH:
${userContext.topCategories?.map(c => `- ${c.name}: $${c.amount} (budget: $${c.budget})`).join('\n') || 'No data yet'}

GUIDELINES:
- Be concise (2-4 sentences for most responses)
- Be specific to their actual numbers, not generic advice
- Be encouraging but honest about areas needing improvement
- For complex questions, offer to go deeper
- Never give tax or legal advice — recommend a professional
- ${userContext.plan === 'free' ? 'Gently remind free users of Pro features when relevant' : ''}
`

// ─── Backend route (api/ai/chat.js) ───────────────────────
export const AI_BACKEND_ROUTE = `
import Anthropic from '@anthropic-ai/sdk'

const client = new Anthropic({ apiKey: process.env.ANTHROPIC_API_KEY })

export default async function handler(req, res) {
  const { message, userContext, conversationHistory } = req.body

  // Plan gating — check in DB, don't trust client
  const { plan, aiQueriesThisMonth } = await getUserPlanFromDB(userContext.userId)
  if (plan === 'free' && aiQueriesThisMonth >= 5) {
    return res.status(403).json({ error: 'upgrade_required', message: 'Upgrade to Pro for unlimited AI coaching.' })
  }
  if (plan === 'pro' && aiQueriesThisMonth >= 20) {
    return res.status(403).json({ error: 'upgrade_required', message: 'Upgrade to Premium for unlimited AI coaching.' })
  }

  const response = await client.messages.create({
    model: 'claude-sonnet-4-20250514',
    max_tokens: 1000,
    system: buildSystemPrompt(userContext),
    messages: [
      ...conversationHistory,
      { role: 'user', content: message }
    ],
  })

  // Increment usage counter in DB
  await incrementAiUsage(userContext.userId)

  res.json({ reply: response.content[0].text })
}
`
